import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, Clock, Award } from 'lucide-react';

interface TrustBadgesProps {
  variant?: 'default' | 'compact' | 'footer';
  className?: string;
}

const badges = [
  {
    icon: Shield,
    title: 'SSL Secured',
    description: '256-bit encryption',
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
  },
  {
    icon: CheckCircle,
    title: 'Verified Business',
    description: 'Registered company',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  {
    icon: Clock,
    title: '24hr Response',
    description: 'Quick turnaround',
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
  },
  {
    icon: Award,
    title: 'Quality Guaranteed',
    description: 'Satisfaction assured',
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
  },
];

/**
 * Trust signal badges for building credibility.
 * Multiple variants for different page contexts.
 */
const TrustBadges = memo(function TrustBadges({
  variant = 'default',
  className = '',
}: TrustBadgesProps) {
  if (variant === 'footer') {
    return (
      <div className={`flex flex-wrap items-center gap-4 ${className}`}>
        {badges.slice(0, 4).map((badge, index) => (
          <div
            key={badge.title}
            className="flex items-center gap-2 text-gray-400"
          >
            <badge.icon className={`w-4 h-4 ${badge.color}`} />
            <span className="text-sm">{badge.title}</span>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`flex flex-wrap items-center justify-center gap-6 ${className}`}>
        {badges.map((badge, index) => (
          <motion.div
            key={badge.title}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="flex items-center gap-2"
          >
            <div className={`w-8 h-8 rounded-lg ${badge.bgColor} flex items-center justify-center`}>
              <badge.icon className={`w-4 h-4 ${badge.color}`} />
            </div>
            <span className="text-sm font-medium text-foreground">{badge.title}</span>
          </motion.div>
        ))}
      </div>
    );
  }

  // Default variant - full cards
  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${className}`}>
      {badges.map((badge, index) => (
        <motion.div
          key={badge.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-muted/30 rounded-xl p-4 text-center border border-border hover:border-primary/30 transition-colors"
        >
          <div className={`w-12 h-12 rounded-xl ${badge.bgColor} flex items-center justify-center mx-auto mb-3`}>
            <badge.icon className={`w-6 h-6 ${badge.color}`} />
          </div>
          <h4 className="font-semibold text-sm mb-1">{badge.title}</h4>
          <p className="text-xs text-muted-foreground">{badge.description}</p>
        </motion.div>
      ))}
    </div>
  );   
});

export default TrustBadges;
